// App.js
import React, { useState, useEffect } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import Browser from "./Browser";
import {
  NotificationProvider,
  PermissionCenterProvider,
  AuthLayout,
  DarkModeProvider,
} from "@1823-partners/core";
import api, { getMyPermissions } from "./api";


/*
 * MUI Chip bug workaround, applied at the theme so it also covers chips this app does not render.
 * Chip's root slot wires up
 *   onClick: event => { handlers.onClick?.(event); onClick(event); }
 * with NO guard on the second call, so clicking a Chip that was given no handler throws
 * "onClick is not a function" and takes the tab down.
 *
 * The chips that hit this are the Granted/Missing labels inside core's permissions drawer, which
 * cannot be patched from here -- the drawer ships compiled inside @1823-partners/core. MUI applies
 * defaultProps to every instance of a component, including ones rendered from another bundle, so
 * supplying the no-op here reaches them.
 *
 * clickable:false is the other half and is not redundant: MUI computes
 *   clickable = clickableProp !== false && onClick ? true : clickableProp
 * so the no-op alone would promote every chip in the app to a ButtonBase with a ripple and a
 * pointer cursor. This app has no genuinely clickable chip; one added later must pass clickable
 * explicitly.
 *
 * Module scope, not inline: DarkModeProvider memoises the theme on this object's identity.
 */
const THEME_OPTIONS = {
  components: {
    MuiChip: { defaultProps: { clickable: false, onClick: () => {} } },
  },
};

export default function App() {
  return (
    <DarkModeProvider appName="browser" themeOptions={THEME_OPTIONS}>
      <NotificationProvider>
        {/* Mounts the permissions drawer (the toolbar's shield button) and, because it wraps
            PermissionRequestProvider itself, keeps turning the "Request access" button on
            permission-denied toasts into an in-app request. Do not also mount
            PermissionRequestProvider -- that nests a second copy. Inside NotificationProvider
            because the request path reports its outcome as a toast. */}
        <PermissionCenterProvider api={api} appName="browser">
          <AuthLayout title="1823 S3 Browser">
            {() => <AuthGate />}
          </AuthLayout>
        </PermissionCenterProvider>
      </NotificationProvider>
    </DarkModeProvider>
  );
}

function AuthGate() {
  // UI hint only — the server enforces every gate via @require_permission.
  // Browsing/downloading needs browser.files:read; the upload drop-zone
  // additionally needs browser.files:write.
  const [permissions, setPermissions] = useState(null);

  useEffect(() => {
    getMyPermissions()
      .then((res) => setPermissions(res.data?.permissions || []))
      .catch(() => setPermissions([]));
  }, []);

  if (permissions === null) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!permissions.includes('browser.files:read')) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom>Access Denied</Typography>
        <Typography color="text.secondary">
          You do not have permission to access this application.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Browser canUpload={permissions.includes('browser.files:write')} />
    </Box>
  );
}
